import { postGetTranslations } from '../../apis/translations';

/**
 * Maps translation keys to their values, using the key as fallback if translation is missing
 * @param {Object} translationKeys - Object with property names as keys and translation key strings as values
 * @param {Object} translations - Object with translation key strings as keys and translated values as values
 * @returns {Object} Object with property names as keys and translated values (or keys as fallback)
 */
function mapTranslations(translationKeys, translations) {
    const result = {};
    for (const [key, translationKey] of Object.entries(translationKeys)) {
        result[key] = translations[translationKey] ?? translationKey;
    }
    return result;
}

export async function fetchProductTranslations() {

    const translationKeys = {
        ListPrice: 'ProjectBuilder.Label.ListPrice',
        Discontinued: 'Product.Label.Discontinued',
        NewReplacement: 'Product.Btn.NewReplacement',
        UnauthenticatedPriceMessage: 'Product.Label.UnauthenticatedPriceMessage',
        MSRP: 'ProjectBuilder.Label.MSRP'
    }

    return postGetTranslations(Object.values(translationKeys))
        .then((translations) => {
            return mapTranslations(translationKeys, translations);
        })
        .catch((error) => {
            console.error('Error fetching translations:', error);
            return mapTranslations(translationKeys, {});
        });
}

export async function fetchShoppingCartTranslations() {
    const translationKeys = {
        AddToCart: 'ShoppingCart.Label.AddToCart',
        AddedToCart: 'ShoppingCart.Label.AddedToCart',
        RemoveFromCart: 'ShoppingCart.Label.RemoveFromCart',
        OutOfStock: 'ShoppingCart.Label.OutOfStock',
        OverInventory: 'ShoppingCart.Message.OverInventory',
        MinQuantity: 'ShoppingCart.Message.MinQuantity',
        AddSuccess: 'ShoppingCart.Message.AddSuccess',
        AddError: 'ShoppingCart.Message.AddError',
        LoginRequired: 'ShoppingCart.Message.LoginRequired',
        RemoveSuccess: 'ShoppingCart.Message.RemoveSuccess',
        RemoveError: 'ShoppingCart.Message.RemoveError'
    };

    return postGetTranslations(Object.values(translationKeys))
        .then((translations) => {
            return mapTranslations(translationKeys, translations);
        })
        .catch((error) => {
            console.error('Error fetching shopping cart translations:', error);
            return mapTranslations(translationKeys, {});
        });
}

export async function fetchShoppingCartPageTranslations() {
    const translationKeys = {
        Empty: 'ShoppingCart.Page.Empty',
        Subtotal: 'ShoppingCart.Page.Subtotal',
        ItemSingular: 'ShoppingCart.Page.ItemSingular',
        ItemPlural: 'ShoppingCart.Page.ItemPlural',
        Checkout: 'ShoppingCart.Button.Checkout',
        Qty: 'ShoppingCart.Label.Qty',
        CheckoutIssue: 'ShoppingCart.Message.CheckoutIssue',
        MaxQuantityReached: 'ShoppingCart.Message.MaxQuantityReached',
        UpdateError: 'ShoppingCart.Message.UpdateError',
        RemoveSuccess: 'ShoppingCart.Message.RemoveSuccess',
        RemoveError: 'ShoppingCart.Message.RemoveError',
        LoadError: 'ShoppingCart.Message.LoadError'
    };

    return postGetTranslations(Object.values(translationKeys))
        .then((translations) => {
            return mapTranslations(translationKeys, translations);
        })
        .catch((error) => {
            console.error('Error fetching shopping cart page translations:', error);
            return mapTranslations(translationKeys, {});
        });
}

export async function fetchSubmitOrderTranslations() {
    const translationKeys = {
        ConfirmCompleteOrder: 'ShoppingCart.SubmitOrder.Message.ConfirmCompleteOrder',
        OrderSubmitted: 'ShoppingCart.SubmitOrder.Message.OrderSubmitted',
        SubmitOrderError: 'ShoppingCart.SubmitOrder.Message.SubmitOrderError',
        PriceChanged: 'ShoppingCart.SubmitOrder.Message.PriceChanged',
        PriceUnavailable: 'ShoppingCart.SubmitOrder.Message.PriceUnavailable',
        Country: 'ShoppingCart.SubmitOrder.Label.Country',
        State: 'ShoppingCart.SubmitOrder.Label.State',
        FirstName: 'ShoppingCart.SubmitOrder.Label.FirstName',
        LastName: 'ShoppingCart.SubmitOrder.Label.LastName',
        Email: 'ShoppingCart.SubmitOrder.Label.Email',
        PhoneNumber: 'ShoppingCart.SubmitOrder.Label.PhoneNumber',
        StreetAddress: 'ShoppingCart.SubmitOrder.Label.StreetAddress',
        AddressLine2: 'ShoppingCart.SubmitOrder.Label.AddressLine2',
        City: 'ShoppingCart.SubmitOrder.Label.City',
        PostalCode: 'ShoppingCart.SubmitOrder.Label.PostalCode',
        SpecialRequirements: 'ShoppingCart.SubmitOrder.Label.SpecialRequirements',
        DisclaimerInformation: 'ShoppingCart.SubmitOrder.Label.DisclaimerInformation',
        AgreeDisclaimer: 'ShoppingCart.SubmitOrder.Label.AgreeDisclaimer',
        BackToCart: 'ShoppingCart.SubmitOrder.Label.BackToCart',
        Submit: 'ShoppingCart.SubmitOrder.Label.Submit',
        UseAccountAddress: 'ShoppingCart.SubmitOrder.Label.UseAccountAddress',
        PoNumber: 'ShoppingCart.SubmitOrder.Label.PoNumber',
        EmailPlaceholder: 'ShoppingCart.SubmitOrder.Placeholder.Email',
        PhonePlaceholder: 'ShoppingCart.SubmitOrder.Placeholder.Phone',
        StreetAddressPlaceholder: 'ShoppingCart.SubmitOrder.Placeholder.StreetAddress',
        AddressLine2Placeholder: 'ShoppingCart.SubmitOrder.Placeholder.AddressLine2',
        CityPlaceholder: 'ShoppingCart.SubmitOrder.Placeholder.City',
        PostalCodePlaceholder: 'ShoppingCart.SubmitOrder.Placeholder.PostalCode'
    };

    return postGetTranslations(Object.values(translationKeys))
        .then((translations) => {
            return mapTranslations(translationKeys, translations);
        })
        .catch((error) => {
            console.error('Error fetching submit order translations:', error);
            return mapTranslations(translationKeys, {});
        });
}